import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../store/store';

/**
 * Acceso a los permisos del usuario autenticado (del JWT, ya en Redux).
 *
 * Etapa 3 del control de acceso: con esto el frontend muestra u oculta
 * acciones. La comprobacion es simple pertenencia en `auth.permissions` — el
 * backend ya resuelve el set exacto por rol (migracion V97).
 *
 * SUPPORT es el rol "break glass": ve y hace todo en el frontend. ADMIN NO se
 * puentea aqui: su JWT ya trae exactamente sus permisos (todos menos los 15
 * marcados delicados), asi que un boton delicado se le oculta correctamente.
 */
const stripRolePrefix = (role: string) =>
  role.startsWith('ROLE_') ? role.slice(5) : role;

export interface PermissionApi {
  permissions: string[];
  isSupport: boolean;
  /** true si tiene ese permiso (o es SUPPORT). */
  can: (permission: string) => boolean;
  /** true si tiene al menos uno. */
  canAny: (permissions: string[]) => boolean;
  /** true si los tiene todos. */
  canAll: (permissions: string[]) => boolean;
}

export const usePermissions = (): PermissionApi => {
  const permissions = useSelector((s: RootState) => s.auth.permissions);
  const roles = useSelector((s: RootState) => s.auth.roles);

  return useMemo(() => {
    const set = new Set(permissions ?? []);
    const isSupport = (roles ?? []).some((r) => stripRolePrefix(r) === 'SUPPORT');

    const can = (permission: string) => isSupport || set.has(permission);
    const canAny = (perms: string[]) =>
      isSupport || perms.some((p) => set.has(p));
    const canAll = (perms: string[]) =>
      isSupport || perms.every((p) => set.has(p));

    return { permissions: [...set], isSupport, can, canAny, canAll };
  }, [permissions, roles]);
};

/** Azucar para un unico permiso: `const puedeAnular = usePermission('SALES_VOID')`. */
export const usePermission = (permission: string): boolean =>
  usePermissions().can(permission);

export default usePermissions;
